import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import i18n from '../../i18n/translate';
import SeeAllFilterButton from '../Filters/SeeAllFilterButton';
import SearchComponent from '../Form/SearchComponent';
import useBreakpointRef from '../../hooks/useBreakpointRef';
import useContainerProps from '../../hooks/useContainerProps';
import useEffectDeep from '../../hooks/useEffectDeep';
import DateFilter from './DateFilter';
import FilterModal from './FilterModal';
import FilterChips from './FilterChips.js';
import { filtersArrayToKeyValueObject, isFilterActive, getInitialFilters, clearAllFilters, removeFilterFromCollection } from './functions';
import noop from '../../util/noop';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';

const BREAKPOINTS = Object.freeze([576, 768, 1080, 1280, 1480, 1788]);
const BREAKPOINT_VALUES = Object.freeze([
  {
    numberOfFilter: 1,
    chipsStyle: { 'margin-top': '4px' },
    searchFullWidth: true,
    searchStyle: { 'flex-basis': '100%', 'flex-wrap': 'wrap-reverse', 'margin-bottom': '24px' }
  }, {
    numberOfFilter: 1,
    chipsStyle: { 'margin-top': '4px' }
  }, {
    numberOfFilter: 1,
    chipsStyle: { 'margin-top': '8px' }
  }, {
    numberOfFilter: 2,
    chipsStyle: { 'margin-top': '8px' }
  }, {
    numberOfFilter: 3,
    chipsStyle: { 'margin-top': '8px' }
  }, {
    numberOfFilter: 4,
    chipsStyle: { 'margin-top': '8px' }
  }, {
    numberOfFilter: 5,
    chipsStyle: { 'margin-top': '8px' }
  }]);

const FilterBar = props => {
  props = useContainerProps(props);

  const { breakpointValue } = useBreakpointRef({
    breakpoints: BREAKPOINTS,
    breakpointValues: BREAKPOINT_VALUES
  });

  const {
    filterConfig,
    filterModalProps,
    initialFilters,
    onSelectFilters = noop,
    onClearFilters = noop,
    searchBox
  } = props;
  const [filters, setFilters] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const activeFilters = filters.filter(filter => isFilterActive(filter));
  const showingPartialFilters = !!(breakpointValue && filters.length > breakpointValue.numberOfFilter);
  const childRef = useRef();

  useEffectDeep(() => {
    const newFilters = getInitialFilters(filterConfig, initialFilters);

    // only set filters from config if something changed dynamically
    if (!isEqual(filters, newFilters)) {
      setFilters(newFilters);
    }
  }, [filterConfig]);

  useEffect(() => {
    // execute passed onChange every time filters are updated
    onSelectFilters(filtersArrayToKeyValueObject(activeFilters));
  }, [filters]);

  const clearFilters = () => {
    const newFilters = clearAllFilters(filters);
    setFilters(newFilters);
    onClearFilters();
  }

  const removeFilter = filterToRemove => {
    const newFilters = removeFilterFromCollection(filterToRemove, filters);

    setFilters(newFilters);
  }

  const handleFilterChange = (updatedFilter, value) => {
    const newFilters = filters.map(filter => {
      if (filter.name === updatedFilter.name) {
        if (filter.type === DateFilter) {
          filter.value = value ? { ...filter.value, ...value } : null;
          if (filter.value && Object.values(filter.value).every(val => val === null)) {
            filter.value = null;
          }
        } else {
          filter.value = value;
        }
      }
      return filter;
    });

    setFilters(newFilters);
  }

  const showAllFilters = () => {
    setShowFilterModal(true);
  }

  const onModalApplyFilters = updatedFilters => {
    if (!Object.keys(updatedFilters).length) {
      // empty object means all filters were removed
      clearFilters();
    } else {
      // update filter values
      let newFilters = [...filters];
      Object.entries(updatedFilters).forEach(([name, value]) => {
        newFilters = newFilters.map(filter => {
          if (filter.name === name) {
            filter.value = value;
          }

          return filter;
        })
      });

      setFilters(newFilters);
    }

    setShowFilterModal(false);
  }

  const renderFiltersAndSearch = () => {
    // to make adjustment as seeAllButton is considered as a filter pill as per the design
    const breakpointUXAdjustment = 1;
    const visibleFilters = filters.slice(0, breakpointValue && showingPartialFilters ? breakpointValue.numberOfFilter - breakpointUXAdjustment : filters.length);

    return (
      <div className="filters-search">
        <div className="filters">
          {visibleFilters.map(filter => {
            const FilterComponent = filter.type;

            return (
              <FilterComponent
                {...filter}
                ref={childRef}
                key={filter.name}
                label={filter.label}
                onChange={(value, action) => handleFilterChange(filter, value, action)}
                disabled={props.disabled || !!filter.disabled}
                active={isFilterActive(filter)}
              />
            );
          })}

          {showingPartialFilters && <SeeAllFilterButton onClick={showAllFilters} disabled={props.disabled} />}

          <div className="clear-all">
            {activeFilters.length > 0 && (
              <Button
                onClick={clearFilters}
                style="link"
                className="clear-filters"
                data-action="clear-filters"
                icon="close"
                label={i18n.t('Clear Filters')}
                disabled={props.disabled}
              />
            )}
          </div>
        </div>
        {renderSearch()}
      </div>
    );
  }

  const renderSearch = () => {
    if (!searchBox) return null;

    const searchBoxDataAttributes = {};
    if (searchBox?.data?.action) {
      searchBoxDataAttributes['data-action'] = searchBox.data.action;
    }
    if (searchBox?.data?.id) {
      searchBoxDataAttributes['data-object-id'] = searchBox.data.id;
    }
    if (searchBox?.data?.type) {
      searchBoxDataAttributes['data-object-type'] = searchBox.data.type;
    }
    return (
      <div
        {...searchBoxDataAttributes}
        className="search"
        style={breakpointValue && breakpointValue.searchStyle}
      >
        <SearchComponent
          searchAction={searchBox.searchAction}
          placeholder={searchBox.placeholder || i18n.t('Search')}
          valueChangeDelay={searchBox.valueChangeDelay}
          wrapperClass={breakpointValue && breakpointValue.searchFullWidth ? 'full-width' : undefined}
          value={searchBox.value}
        />
      </div>
    )
  }

  const renderFilterModal = () => {
    if (!showFilterModal) {
      return null;
    }

    return (
      <FilterModal
        backdropClassName={filterModalProps?.backdropClassName}
        className={filterModalProps?.className}
        filterConfig={filters.map(filter => omit(filter, 'value'))}
        initialFilters={filtersArrayToKeyValueObject(activeFilters)}
        onSelectFilters={onModalApplyFilters}
        onClose={() => setShowFilterModal(false)}
        show={true}
        title={i18n.t('Filters')}
      />
    );
  }

  return (
    <>
      <div className="filter-bar">
        {renderFiltersAndSearch()}
        <div className="break" />
        <FilterChips
          filters={activeFilters}
          onRemoveFilter={removeFilter}
          style={breakpointValue && breakpointValue.chipsStyle}
          disabled={props.disabled}
        />
      </div>
      {renderFilterModal()}
    </>
  );
}

FilterBar.propTypes = {
  disabled: PropTypes.bool,
  filterConfig: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.element.isRequired,
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired
  })),
  filterModalProps: PropTypes.object,
  initialFilters: PropTypes.object,
  onClearFilters: PropTypes.func,
  onSelectFilters: PropTypes.func,
  searchBox: PropTypes.shape({
    data: PropTypes.shape({
      action: PropTypes.string,
      id: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.string
    }),
    value: PropTypes.string,
    isOnlySearch: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    placeholder: PropTypes.string,
    searchAction: PropTypes.func.isRequired,
    valueChangeDelay: PropTypes.number
  })
};

FilterBar.defaultProps = {
  filterConfig: [],
  initialFilters: {}
};

export default FilterBar;
